import { useState } from 'react';
import { brandTemplateCreate, brandTemplateUpdate } from '@goldcast/api/content';
import TemplatePreview from '../Templates/TemplatePreview';
import { getSaveTemplatePayload, mapCurrentClipToTemplate } from '../Templates/utils';
import { useTemplateThumbnails } from '../Templates/useTemplateThumbnails';
import { PreviewTemplate } from '../types';
import LabeledInput from '@/components/atoms/LabeledInput';
import Button from '@/components/atoms/Button/Button';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import Loader from '@/components/atoms/Loader';
import { showErrorToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useAnalytics from '@/hooks/useAnalytics';

export default function NewTemplateModal({
  templateToEdit = null,
  onSave,
  shouldShowIntroOutroWarningText = false
}: {
  onSave: () => void;
  templateToEdit?: PreviewTemplate | null;
  shouldShowIntroOutroWarningText?: boolean;
}) {
  const { clipId } = useClipsContext();

  const template = mapCurrentClipToTemplate(clipId);
  const { thumbnailMap, absoluteSourceTime, currentTime } = useTemplateThumbnails();
  const [templateTitle, setTemplateTitle] = useState<string>(templateToEdit?.title || '');
  const [isNameInvalid, setIsNameInvalid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { logger } = useAppContext();
  const { trackTemplateCreated } = useAnalytics();

  function save() {
    if (!templateTitle) {
      setIsNameInvalid(true);
      return;
    }
    setIsSaving(true);
    templateToEdit ? updateTemplate(templateToEdit) : storeTemplate();
  }

  function storeTemplate() {
    brandTemplateCreate({
      body: getSaveTemplatePayload({ ...template, title: templateTitle }, [])
    })
      .then(result => {
        trackTemplateCreated({
          templateId: (result as any).id,
          templateName: templateTitle,
          layout: template.layout
        });
        onSaveCompleted();
      })
      .catch(err => {
        logger.error(err);
        showErrorToast('Uh-oh! We encountered an issue saving your template. Please check your inputs and try again.');
      });
  }

  function updateTemplate(templateToEdit: PreviewTemplate) {
    brandTemplateUpdate({
      id: templateToEdit.id as string,
      body: getSaveTemplatePayload({ ...templateToEdit, title: templateTitle }, [])
    })
      .then(() => onSaveCompleted())
      .catch(err => {
        logger.error(err);
        showErrorToast(
          'Looks like updating the template hit a snag. Double-check your changes and give it another whirl.'
        );
      });
  }

  function onSaveCompleted() {
    onSave();
    EventBus.dispatch(CustomEvents.ReloadTemplates);
  }

  return (
    <div className="h-full">
      <div className="h-full overflow-y-scroll">
        <div className="flex h-full flex-col">
          <div className="relative">
            <div className="mt-4 px-4">
              <TemplatePreview
                template={{ ...(templateToEdit ? templateToEdit : template) }}
                canSelect={false}
                classNames="p-10"
                thumbnailMap={thumbnailMap}
                absoluteSourceTime={absoluteSourceTime}
                currentTime={currentTime}
                scale={3}
              />
            </div>
            <div className="mt-4 px-4">
              <LabeledInput
                label="Template Name"
                classNames="w-full pb-2 p-px"
                value={templateTitle}
                onChange={setTemplateTitle}
                data-testid="template-first-name"
                isRequired={true}
                maxLength={100}
                isInvalid={isNameInvalid}
              />

              {shouldShowIntroOutroWarningText && (
                <div className="mb-3 text-xs text-red-500">
                  <sup>*</sup> Template will be saved without intro/outro.
                </div>
              )}
            </div>
          </div>
          <div className="mt-auto flex flex-row justify-end border-t">
            <Button
              disabled={isSaving}
              onClick={save}
              variation="filled"
              className="m-2"
              trackingId="save-template-button"
            >
              {isSaving && <Loader size="xs" />}
              Save Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
